import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAiJobs } from "@/lib/ai-jobs";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, CheckCircle2, Sparkles, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";

const BLANK_Q = () => ({ text: "", options: ["", "", "", ""], correct_index: 0 });

export default function QuizDialog({ open, onOpenChange, chapterId, chapterTitle, onSaved }) {
    const { trackJob } = useAiJobs();
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [genStats, setGenStats] = useState(null);

    const reloadQuestions = () => {
        if (!chapterId) return;
        setLoading(true);
        return api.get(`/chapters/${chapterId}/quiz`)
            .then((r) => {
                const qs = (r.data?.questions || []).map((q) => ({
                    text: q.text || "",
                    options: q.options?.length === 4 ? q.options : ["", "", "", ""],
                    correct_index: q.correct_index ?? 0,
                }));
                setQuestions(qs.length ? qs : [BLANK_Q()]);
            })
            .catch(() => setQuestions([BLANK_Q()]))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        if (!open || !chapterId) return;
        reloadQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, chapterId]);

    const updateQ = (idx, patch) => {
        setQuestions((qs) => qs.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
    };
    const updateOption = (qIdx, optIdx, value) => {
        setQuestions((qs) =>
            qs.map((q, i) =>
                i === qIdx
                    ? { ...q, options: q.options.map((o, j) => (j === optIdx ? value : o)) }
                    : q,
            ),
        );
    };
    const addQ = () => setQuestions((qs) => [...qs, BLANK_Q()]);
    const removeQ = (idx) => setQuestions((qs) => qs.filter((_, i) => i !== idx));

    const autoGenerate = async () => {
        if (!chapterId) return;
        setGenerating(true);
        setGenStats(null);
        try {
            // Kick off the job (returns immediately with a job_id) then hand
            // off to the global AI-jobs tray. The dialog closes so the user
            // can keep working on other chapters — they reopen this dialog
            // to review the drafted questions once the tray says "done".
            const { data: kick } = await api.post(
                `/chapters/${chapterId}/quiz/auto-generate`,
            );
            const jobId = kick?.job_id;
            if (!jobId) {
                toast.error("Could not start AI generation. Try again.");
                return;
            }
            trackJob({
                kind: "quiz",
                jobId,
                chapterId,
                chapterTitle: chapterTitle || kick.chapter_title || "Chapter",
                numLessons: kick.total || undefined,
                onDone: (data) => {
                    // Refresh the underlying chapter so the "Edit quiz" button
                    // flips from "Add quiz" → "Edit quiz" without a full reload.
                    onSaved?.();
                    setGenStats(data?.stats || null);
                },
            });
            toast.success(
                `Queued AI quiz for "${chapterTitle || "this chapter"}". Track progress in the bottom-right tray — reopen this dialog to review when done.`,
            );
            onOpenChange(false);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "AI quiz generation failed.",
            );
        } finally {
            setGenerating(false);
        }
    };

    const save = async () => {
        for (const [i, q] of questions.entries()) {
            if (!q.text.trim()) {
                toast.error(`Question ${i + 1} has no text`);
                return;
            }
            if (q.options.some((o) => !o.trim())) {
                toast.error(`Question ${i + 1} has empty options`);
                return;
            }
        }
        setSaving(true);
        try {
            await api.put(`/chapters/${chapterId}/quiz`, { questions });
            toast.success("Quiz saved");
            onOpenChange(false);
            onSaved?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="quiz-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display">Chapter quiz</DialogTitle>
                    <p className="text-sm text-muted-foreground">
                        Students must score 14/20 (70%) to unlock the next chapter. The
                        answer key below is only ever shown to instructors and admins —
                        students see questions &amp; options without it.
                    </p>
                </DialogHeader>

                <div
                    className="rounded-md border border-primary/20 bg-primary/5 px-3 py-3 flex items-start gap-3"
                    data-testid="quiz-auto-generate-card"
                >
                    <Sparkles className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-foreground">
                            Auto-generate quiz from chapter content
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5">
                            AI scans every lesson body and PDF attachment in this
                            chapter (with OCR fallback for scanned pages) and drafts
                            20 multiple-choice questions with the answer key.{" "}
                            <strong>This dialog closes when you click — progress
                            shows in the bottom-right tray.</strong> Reopen this
                            dialog to review &amp; save once the tray says done.
                        </p>
                        {genStats ? (
                            <p
                                className="text-[11px] text-primary/80 mt-1"
                                data-testid="quiz-gen-stats"
                            >
                                Last run: {genStats.lessons_scanned} lessons ·{" "}
                                {genStats.pdf_attachments_scanned} PDF
                                {genStats.pdf_attachments_scanned === 1 ? "" : "s"}
                                {genStats.ocr_used ? " · OCR used" : ""} ·{" "}
                                {Math.round((genStats.total_chars || 0) / 1000)}k chars
                            </p>
                        ) : null}
                    </div>
                    <Button
                        type="button"
                        size="sm"
                        onClick={autoGenerate}
                        disabled={generating || loading || saving}
                        className="ai-shimmer gap-1.5 flex-shrink-0"
                        data-testid="quiz-auto-generate-btn"
                    >
                        {generating ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Sparkles className="h-3.5 w-3.5" />
                        )}
                        <span className="text-xs font-semibold">
                            {generating ? "Starting…" : "AI: Draft 20 Qs"}
                        </span>
                    </Button>
                </div>

                <div
                    className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.14em] text-muted-foreground mt-2"
                    data-testid="quiz-answer-bank-label"
                >
                    <Lock className="h-3 w-3" />
                    Answer bank — instructor / admin only
                </div>

                {loading ? (
                    <div className="py-12 text-center text-muted-foreground">Loading…</div>
                ) : (
                    <div className="space-y-5">
                        {questions.map((q, qIdx) => (
                            <div
                                key={qIdx}
                                className="border border-border rounded-sm p-4 bg-secondary/20"
                                data-testid={`quiz-q-${qIdx}`}
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <Label className="font-semibold">Question {qIdx + 1}</Label>
                                    {questions.length > 1 && (
                                        <Button
                                            size="icon"
                                            variant="ghost"
                                            onClick={() => removeQ(qIdx)}
                                            data-testid={`quiz-q-del-${qIdx}`}
                                        >
                                            <Trash2 className="h-4 w-4 text-destructive" />
                                        </Button>
                                    )}
                                </div>
                                <Textarea
                                    rows={2}
                                    value={q.text}
                                    onChange={(e) => updateQ(qIdx, { text: e.target.value })}
                                    placeholder="Question text"
                                    data-testid={`quiz-q-text-${qIdx}`}
                                />
                                <div className="mt-3 space-y-2">
                                    {q.options.map((opt, optIdx) => (
                                        <div key={optIdx} className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    updateQ(qIdx, { correct_index: optIdx })
                                                }
                                                className="flex-shrink-0"
                                                data-testid={`quiz-correct-${qIdx}-${optIdx}`}
                                                aria-label={`Mark option ${optIdx + 1} correct`}
                                            >
                                                <CheckCircle2
                                                    className={`h-5 w-5 ${
                                                        q.correct_index === optIdx
                                                            ? "text-accent fill-accent/20"
                                                            : "text-muted-foreground"
                                                    }`}
                                                />
                                            </button>
                                            <Input
                                                value={opt}
                                                onChange={(e) =>
                                                    updateOption(qIdx, optIdx, e.target.value)
                                                }
                                                placeholder={`Option ${optIdx + 1}`}
                                                data-testid={`quiz-opt-${qIdx}-${optIdx}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}

                        <Button
                            variant="outline"
                            onClick={addQ}
                            className="w-full"
                            data-testid="quiz-add-q-btn"
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Add question
                        </Button>
                    </div>
                )}

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)}>
                        Cancel
                    </Button>
                    <Button
                        onClick={save}
                        disabled={saving || loading}
                        className="bg-primary hover:bg-primary/90"
                        data-testid="quiz-save-btn"
                    >
                        {saving ? "Saving…" : "Save quiz"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
